import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import { icon } from "../../../../config";
import UserProfileCard from "../../../../shared/components/UserProfileCard/UserProfileCard";
import { useProcessedFields } from "../../../../shared/components/UserProfileCard/userProfileCard.hook";
import { getUserDetailsByUserId, searchEmailContacts } from "../../../../shared/services/user.service";
import "./EmailRecipientInput.scss";

/**
 * Email Recipient Input Component with Gmail-style chips
 * @param {Object} props
 * @param {Array} props.recipients - Array of recipient objects
 *   - From dropdown: full contact object (Name, Email, PhotoLink, PhotoLinks, TemporaryContact, etc.)
 *   - Manually typed: {Name: string, Email: string, isNew: 1}
 * @param {Function} props.setRecipients - Function to update recipients
 * @param {string} props.label - Label for the field (e.g., "CC", "BCC")
 * @param {string} props.placeholder - Placeholder text
 * @param {Function} props.onValidationError - Callback when validation fails
 * @param {Function} props.onInputChange - Callback to expose input value to parent
 * @returns JSX
 * @author Sejal
 */
const EmailRecipientInput = ({ recipients = [], setRecipients, label, placeholder = "Add recipients...", onValidationError, onInputChange }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileCardPosition, setProfileCardPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const profileCardRef = useRef(null);

  const { user } = useSelector((state) => state.auth);

  /**
   * Validate email format
   * @param {string} email - Email address to validate
   * @returns {boolean} - True if valid email format
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  /**
   * Show validation error via callback
   */
  const showValidationError = (email, field) => {
    if (onValidationError) {
      onValidationError(email, field);
    }
  };

  /**
   * Handle click outside to close dropdown
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /**
   * Debounced search function
   */
  const debouncedSearch = useCallback(
    _.debounce(async (searchText) => {
      if (searchText.trim().length > 0) {
        const results = await searchEmailContacts(searchText);
        setSuggestions(results || []);
        setShowDropdown(true);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    }, 300),
    []
  );

  /**
   * Handle input change
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedIndex(-1);
    debouncedSearch(value);
    // Notify parent of input value change
    if (onInputChange) {
      onInputChange(value);
    }
  };

  /**
   * Add recipient
   * @param {string} email - Email address
   * @param {string} name - Optional name (for dropdown selections)
   * @param {boolean} isFromDropdown - Whether the recipient was selected from dropdown
   * @param {Object} contactObject - Full contact object from dropdown (if applicable)
   */
  const addRecipient = (email, name = null, isFromDropdown = false, contactObject = null) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      return;
    }
    // Show error for invalid email
    if (!isValidEmail(trimmedEmail)) {
      showValidationError(trimmedEmail, label || "To");
      setInputValue("");
      if (onInputChange) {
        onInputChange("");
      }
      return;
    }
    const emailExists = recipients.some((recipient) => {
      const recipientEmail = recipient.Email || recipient.email;
      return recipientEmail === trimmedEmail;
    });
    if (emailExists) {
      setInputValue("");
      if (onInputChange) {
        onInputChange("");
      }
      return;
    }
    let newRecipient;
    if (isFromDropdown && contactObject) {
      newRecipient = contactObject;
    } else {
      const lowerCaseEmail = trimmedEmail.toLowerCase();
      newRecipient = {
        Name: lowerCaseEmail,
        Email: lowerCaseEmail,
        isNew: 1,
      };
    }
    setRecipients([...recipients, newRecipient]);
    setInputValue("");
    if (onInputChange) {
      onInputChange("");
    }
    setSuggestions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  /**
   * Remove recipient
   */
  const removeRecipient = (emailToRemove) => {
    setRecipients(
      recipients.filter((recipient) => {
        const recipientEmail = recipient.Email || recipient.email;
        return recipientEmail !== emailToRemove;
      })
    );
  };

  /**
   * Handle key down events
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        const contact = suggestions[selectedIndex];
        addRecipient(contact.Email || contact.email, contact.Name || contact.name, true, contact); // From dropdown
      } else if (inputValue.trim()) {
        addRecipient(inputValue, null, false); // Manual entry
      }
    } else if (e.key === "Backspace" && !inputValue && recipients.length > 0) {
      const lastRecipient = recipients[recipients.length - 1];
      removeRecipient(lastRecipient.email || lastRecipient.Email);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setSelectedIndex(-1);
    } else if (e.key === "," || e.key === ";") {
      e.preventDefault();
      if (inputValue.trim()) {
        addRecipient(inputValue, null, false); // Manual entry
      }
    }
  };

  /**
   * Handle suggestion click
   */
  const handleSuggestionClick = (contact) => {
    addRecipient(contact.Email || contact.email, contact.Name || contact.name, true, contact); // From dropdown
  };

  /**
   * Handle input blur
   */
  const handleBlur = () => {
    // Add current input as recipient if it's a valid email format
    setTimeout(() => {
      if (inputValue.trim()) {
        if (isValidEmail(inputValue)) {
          addRecipient(inputValue, null, false); // Manual entry
        } else {
          // Don't show error on blur - user might be clicking on dropdown
          // Just clear the invalid input silently
          setInputValue("");
          if (onInputChange) {
            onInputChange("");
          }
        }
      }
    }, 200);
  };

  /**
   * Get initials from name
   */
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Fetch user details when chip is clicked
   */
  const handleChipClick = async (recipient, event) => {
    if (recipient.isNew || recipient.Type !== "user" || !recipient.Id) {
      return;
    }
    const chipElement = event.currentTarget.closest(".email-chip");
    if (chipElement) {
      const rect = chipElement.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();

      setProfileCardPosition({
        top: rect.bottom - (containerRect?.top || 0) + 5, // 5px gap below chip
        left: rect.left - (containerRect?.left || 0),
      });
    }
    setLoadingProfile(true);
    setShowProfileCard(true);
    try {
      const userId = recipient.Id;
      const companyId = user?.companyId;
      const response = await getUserDetailsByUserId({ uId: userId, companyId: companyId });
      if (response?.success) {
        let userData = response.data;
        if (userData.UserDetails?.PhotoLinks) {
          userData.UserDetails.PhotoLinks = userData.UserDetails.PhotoLinks?.small || userData.UserDetails.PhotoLinks;
        }
        setUserDetails(userData);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  /**
   * Close profile card when clicking outside or pressing Escape
   */
  useEffect(() => {
    const handleClickOutsideProfile = (event) => {
      if (profileCardRef.current && !profileCardRef.current.contains(event.target) && !event.target.closest(".email-chip")) {
        setShowProfileCard(false);
        setUserDetails({});
      }
    };
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && showProfileCard) {
        setShowProfileCard(false);
        setUserDetails({});
      }
    };
    if (showProfileCard) {
      document.addEventListener("mousedown", handleClickOutsideProfile);
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("mousedown", handleClickOutsideProfile);
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [showProfileCard]);

  // Process fields for UserProfileCard
  const processedFields = useProcessedFields(userDetails);

  return (
    <div className='email-recipient-input-container' ref={containerRef}>
      <div className='email-recipient-input-wrapper'>
        {label && <strong className='text-size mr-1 align-items-center d-flex mt-1'>{label}</strong>}
        <div className='email-recipient-chips-container' onClick={() => inputRef.current?.focus()}>
          {recipients.map((recipient, index) => {
            const email = recipient.Email || recipient.email;
            const name = recipient.Name || recipient.name;
            const isClickable = !recipient.isNew && recipient.Type === "user" && recipient.Id;
            return (
              <div key={index} className={`email-chip ${recipient.isNew ? "email-chip-manual" : ""} ${isClickable ? "email-chip-clickable" : ""}`}>
                <span
                  className='email-chip-text'
                  title={isClickable ? "Click to view profile" : email}
                  onClick={(e) => isClickable && handleChipClick(recipient, e)}
                  style={{ cursor: isClickable ? "pointer" : "default" }}>
                  {name || email}
                </span>
                <span
                  className='email-chip-remove'
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRecipient(email);
                  }}
                  title='Remove'>
                  {icon.CLOSE}
                </span>
              </div>
            );
          })}
          <input
            ref={inputRef}
            type='text'
            className='email-recipient-input'
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={recipients.length === 0 ? placeholder : ""}
          />
        </div>
      </div>

      {showDropdown && suggestions.length > 0 && (
        <div className='email-suggestions-dropdown' ref={dropdownRef}>
          {suggestions.map((contact, index) => (
            <div
              key={index}
              className={`email-suggestion-item ${selectedIndex === index ? "selected" : ""} ${
                contact.TemporaryContact === 1 ? "temporary-contact" : ""
              }`}
              onClick={() => handleSuggestionClick(contact)}
              onMouseEnter={() => setSelectedIndex(index)}>
              {contact.PhotoLinks ? (
                <div className='brand-logo '>
                  {" "}
                  <img src={`${contact.PhotoLinks.small}`} alt='org' className='logo rounded-circle object-fit-cover margin-right'></img>
                </div>
              ) : (
                <div className='email-suggestion-avatar'>{getInitials(contact.Name || contact.name || contact.Email || contact.email)}</div>
              )}
              <div className='email-suggestion-details'>
                <div className='email-suggestion-name'>{contact.Name || contact.name || contact.Email || contact.email}</div>
                <div className='email-suggestion-email'>{contact.Email || contact.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* User Profile Card Dropdown */}
      {showProfileCard && (
        <div
          className='email-profile-card-dropdown'
          ref={profileCardRef}
          style={{
            top: `${profileCardPosition.top - 15}px`,
            left: `${profileCardPosition.left}px`,
          }}>
          {loadingProfile ? (
            <div className='email-profile-loading'>Loading user profile...</div>
          ) : userDetails?.UserDetails ? (
            <UserProfileCard
              user={userDetails}
              fields={processedFields}
              onActionClick={() => setShowProfileCard(false)}
              actionIcon={icon.CLOSE}
              type={"Email"}
            />
          ) : (
            <div className='email-profile-error'>Unable to load user profile</div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailRecipientInput;
